import { useState } from 'react';
import { Sun, Moon, Monitor, Palette, Flame, Volume2, VolumeX, Copy, Check } from 'lucide-react';
import { useTheme } from '../../store/theme-context';
import { useAudioTheme } from '../../store/audio-theme-context';
import type { ThemeMode, ThemeVariant } from '../../utils/theme';
import { audioVariants, type AudioVariant } from '../../audio/registry';
import { Switch } from '../../components/ui/switch';
import { useAuth } from '../../store/auth-context';
import { useToast } from '../../components/ui/Toast';
import ProfileSection from './ProfileSection';

const modeOptions: { mode: ThemeMode; label: string; description: string; Icon: typeof Sun }[] = [
  { mode: 'light', label: 'Light', description: 'Always use light theme', Icon: Sun },
  { mode: 'dark', label: 'Dark', description: 'Always use dark theme', Icon: Moon },
  { mode: 'system', label: 'System', description: 'Follow browser preference', Icon: Monitor },
];

const variantOptions: { variant: ThemeVariant; label: string; description: string; Icon: typeof Sun }[] = [
  { variant: 'default', label: 'Default', description: 'Sky blue brand palette', Icon: Palette },
  { variant: 'neon', label: 'Neon', description: 'Neon orange on cool dark', Icon: Flame },
];

export default function SettingsPage() {
  const { mode, setMode, variant, setVariant } = useTheme();
  const audio = useAudioTheme();

  const handleAudioToggle = (next: boolean) => {
    audio.setEnabled(next);
    if (next) {
      // Preview cue so the user hears confirmation right after enabling.
      audio.play('thread.response');
    }
  };

  return (
    <div className="max-w-2xl">
      <h1 className="text-2xl font-bold text-foreground">Settings</h1>

      <div className="mt-8">
        <ProfileSection />
      </div>

      <section className="mt-10">
        <h2 className="text-lg font-semibold text-foreground">Appearance</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Choose how the console looks to you.
        </p>

        <div className="mt-4 grid grid-cols-3 gap-4">
          {modeOptions.map(({ mode: m, label, description, Icon }) => {
            const active = mode === m;
            return (
              <button
                key={m}
                onClick={() => setMode(m)}
                className={`focus-ring flex flex-col items-center gap-2 rounded-lg border-2 p-4 text-center transition-colors ${
                  active
                    ? 'border-primary bg-primary/5 ring-2 ring-primary'
                    : 'border-border bg-card hover:bg-muted'
                }`}
              >
                <Icon className={`h-6 w-6 ${active ? 'text-primary' : 'text-muted-foreground'}`} />
                <span className={`text-sm font-medium ${active ? 'text-primary' : 'text-foreground'}`}>
                  {label}
                </span>
                <span className="text-xs text-muted-foreground">{description}</span>
              </button>
            );
          })}
        </div>
      </section>

      <section className="mt-10">
        <h2 className="text-lg font-semibold text-foreground">Color theme</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Switch the brand palette. Changes apply instantly and persist across sessions.
        </p>

        <div className="mt-4 grid grid-cols-2 gap-4">
          {variantOptions.map(({ variant: v, label, description, Icon }) => {
            const active = variant === v;
            return (
              <button
                key={v}
                onClick={() => setVariant(v)}
                className={`focus-ring flex flex-col items-center gap-2 rounded-lg border-2 p-4 text-center transition-colors ${
                  active
                    ? 'border-primary bg-primary/5 ring-2 ring-primary'
                    : 'border-border bg-card hover:bg-muted'
                }`}
              >
                <Icon className={`h-6 w-6 ${active ? 'text-primary' : 'text-muted-foreground'}`} />
                <span className={`text-sm font-medium ${active ? 'text-primary' : 'text-foreground'}`}>
                  {label}
                </span>
                <span className="text-xs text-muted-foreground">{description}</span>
              </button>
            );
          })}
        </div>
      </section>

      <section className="mt-10">
        <h2 className="text-lg font-semibold text-foreground">Audio</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Subtle sound effects when the agent thinks, replies, or task progress changes.
        </p>

        <div className="mt-4 rounded-lg border border-border bg-card divide-y divide-border">
          <div className="flex items-center justify-between gap-4 p-4">
            <div className="flex items-start gap-3">
              {audio.enabled ? (
                <Volume2 className="h-5 w-5 shrink-0 text-primary mt-0.5" />
              ) : (
                <VolumeX className="h-5 w-5 shrink-0 text-muted-foreground mt-0.5" />
              )}
              <div>
                <div className="text-sm font-medium text-foreground">Sound effects</div>
                <div className="text-xs text-muted-foreground">
                  Play short cues for chain-of-thought steps, responses, and tasks.
                </div>
              </div>
            </div>
            <Switch
              checked={audio.enabled}
              onCheckedChange={handleAudioToggle}
              aria-label="Enable sound effects"
            />
          </div>

          <div className="flex items-center justify-between gap-4 p-4">
            <div>
              <div className="text-sm font-medium text-foreground">Volume</div>
              <div className="text-xs text-muted-foreground">
                Master level for all cues.
              </div>
            </div>
            <div className="flex items-center gap-3 w-48">
              <input
                type="range"
                min={0}
                max={100}
                step={1}
                value={Math.round(audio.volume * 100)}
                onChange={(e) => audio.setVolume(Number(e.target.value) / 100)}
                disabled={!audio.enabled}
                className="focus-ring flex-1 h-1.5 cursor-pointer appearance-none rounded-full bg-muted accent-primary disabled:cursor-not-allowed disabled:opacity-50"
                aria-label="Audio volume"
              />
              <span className="w-9 text-right text-xs tabular-nums text-muted-foreground">
                {Math.round(audio.volume * 100)}%
              </span>
            </div>
          </div>

          <div className="flex items-center justify-between gap-4 p-4">
            <div>
              <div className="text-sm font-medium text-foreground">Audio theme</div>
              <div className="text-xs text-muted-foreground">
                Sound pack used for all cues.
              </div>
            </div>
            <select
              value={audio.variant}
              onChange={(e) => audio.setVariant(e.target.value as AudioVariant)}
              disabled={!audio.enabled}
              className="focus-ring rounded-md border border-border bg-background px-3 py-1.5 text-sm text-foreground disabled:cursor-not-allowed disabled:opacity-50"
              aria-label="Audio theme variant"
            >
              {audioVariants.map((opt) => (
                <option key={opt.variant} value={opt.variant}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </section>

      <IdsFooter />

      <p className="mt-3 font-mono text-[10px] text-muted-foreground/70">
        v{import.meta.env.VITE_APP_VERSION} · {import.meta.env.VITE_APP_BUILD}
      </p>
    </div>
  );
}

function IdsFooter() {
  const { user } = useAuth();
  const toast = useToast();
  const [copied, setCopied] = useState<string | null>(null);

  if (!user) return null;

  const copy = async (key: string, label: string, value: string) => {
    await navigator.clipboard.writeText(value);
    setCopied(key);
    toast.success(`${label} copied`);
    setTimeout(() => setCopied((k) => (k === key ? null : k)), 2000);
  };

  return (
    <div className="mt-12 flex flex-wrap gap-x-6 gap-y-1 font-mono text-[10px] text-muted-foreground/70">
      <IdLine
        label="user"
        value={user.userId}
        copied={copied === 'user'}
        onCopy={() => copy('user', 'User ID', user.userId)}
      />
      <IdLine
        label="organization"
        value={user.orgId}
        copied={copied === 'org'}
        onCopy={() => copy('org', 'Organization ID', user.orgId)}
      />
    </div>
  );
}

function IdLine({
  label,
  value,
  copied,
  onCopy,
}: {
  label: string;
  value: string;
  copied: boolean;
  onCopy: () => void;
}) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span>{label}</span>
      <span className="text-muted-foreground">{value}</span>
      <button
        onClick={onCopy}
        className="focus-ring rounded p-0.5 hover:text-foreground"
        aria-label={`Copy ${label} ID`}
      >
        {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
      </button>
    </span>
  );
}
